import { useEffect } from 'react';
import { useStateInRef } from 'seagull';
import { useLogBIEvent } from '~/components/BiFrontendProvider/useLogBIEvent';
import { useIoContext, useStore } from '~/contexts';
import { useEventReporter } from '~/event-reporter';
import { selectors } from '~/store/state-selectors';
import { makePendingLoginMethodStorage } from './pendingLogin';
// WHY: a login outcome is not a click, and the data team's interaction queries filter `type='click'`.
const LOGIN_TELEMETRY_EVENT_TYPE = 'login_success';
const LOGIN_TELEMETRY_ENTITY_TYPE = 'login';
const LOGIN_TELEMETRY_PAGE_NAME = 'login';
const reportLoginSuccess = (param)=>{
    let { userId, method, logBIEvent } = param;
    logBIEvent({
        type: LOGIN_TELEMETRY_EVENT_TYPE,
        page_name: LOGIN_TELEMETRY_PAGE_NAME,
        hierarchy: [],
        entity_type: LOGIN_TELEMETRY_ENTITY_TYPE,
        entity_name: method,
        login_method: method,
        user_id: userId
    });
};
// WHY: every login tears the document down before it finishes, so only the next authenticated render can report.
export function useLoginSuccessReporter() {
    const { sessionStorage, getNow, cookies, browser } = useIoContext();
    const getLogBIEvent = useStateInRef(useLogBIEvent());
    const userInfo = useStore((state)=>state.userInfo);
    const isRealUser = useStore(selectors.isRealUser) && !!userInfo;
    const { sendEventToGtm } = useEventReporter();
    useEffect(()=>{
        // WHY: a login hands the session back on a forward navigation, so neither shape below is that return.
        const abandonedTheLogin = cookies.getAuthToken() == null || browser.wasEnteredByBackForward();
        if (abandonedTheLogin) {
            makePendingLoginMethodStorage(sessionStorage, getNow).clearPendingMethod();
        }
    }, []);
    useEffect(// WHY: coming back from the provider through the bfcache resumes this document, so no effect above re-runs.
    ()=>browser.registerPageRestoredListener(()=>makePendingLoginMethodStorage(sessionStorage, getNow).clearPendingMethod()), []);
    useEffect(()=>{
        if (!isRealUser) return;
        const pendingLogin = makePendingLoginMethodStorage(sessionStorage, getNow);
        const method = pendingLogin.readPendingMethod();
        if (method == null) return;
        pendingLogin.clearPendingMethod();
        const userId = userInfo.id();
        if (userId == null) return;
        sendEventToGtm('login_success', {
            user_id: userId,
            method
        });
        reportLoginSuccess({
            userId,
            method,
            logBIEvent: getLogBIEvent()
        });
    }, [
        userInfo,
        isRealUser
    ]);
}
